import { useState } from "react"
import { ChevronDown, ChevronRight, Check, X } from "lucide-react"
import { cn } from "@/lib/cn"
import { MessageMarkdown } from "@/components/domain/message-markdown"

type Props = {
  /** Tool call (model invoking it) — input shape varies by tool. */
  toolCall?:   { id?: string; name?: string; input?: unknown }
  /** Tool result paired with the call. */
  toolResult?: { id?: string; name?: string; output?: unknown; is_error?: boolean }
  timestamp?:  string | null
}

// Single chip per tool invocation. When BOTH a call and its matching
// result are present we merge them into one expandable box (one row in
// the conversation, two payloads inside). Green when the tool succeeded
// or is still in flight; red on error. The previous gold-vs-green split
// was confusing — every tool use is a single conceptual event from the
// operator's perspective.
export function ToolCallChip({ toolCall, toolResult, timestamp }: Props) {
  const [open, setOpen] = useState(false)
  // Don't render the pre-result "running" chip. The agent's tool_calls
  // and the corresponding tool_results live on different agent_messages
  // (assistant message + subsequent tool message), and showing both
  // sides as separate chips made the convo view look like four ops
  // when there were really two. We only render once the result lands —
  // ✓ on success, ✗ on error.
  if (!toolResult) return null

  const name    = toolCall?.name ?? toolResult.name ?? "tool"
  const isError = toolResult.is_error === true
  // For tools where the action is parameterized by a SINGLE meaningful
  // key (e.g. call_company_endpoint → endpoint_key), surface that key in
  // the chip header so the operator can tell two parallel calls apart
  // without expanding both. Pulled from input.endpoint_key on the
  // call side and content.endpoint on the result side (the runtime
  // mirrors the same string into both for call_company_endpoint).
  const detail = extractToolDetail(name, toolCall?.input, toolResult.output)
  const fallbackRequest = toolCall ? null : extractToolRequest(name, toolResult.output)
  const outputPreview = extractToolOutputPreview(name, toolResult.output)
  // Subtle border + faint tinted background so the chip reads as a
  // chip but doesn't compete with the message bubble for attention.
  // Errors get higher contrast so they actually pop.
  const tone = isError
    ? "border-status-error/25 bg-status-error/[0.06] text-status-error"
    : "border-status-success/15 bg-status-success/[0.04] text-status-success"
  // ✗ on failure, ✓ on success — Wrench branch was for the (now-skipped)
  // pre-result "running" chip and is dead.
  const Icon = isError ? X : Check

  return (
    <div className={cn("rounded-control border px-2 py-1 text-xs", tone)}>
      <button
        type="button"
        className="flex w-full items-center gap-1.5"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown size={11} strokeWidth={2} /> : <ChevronRight size={11} strokeWidth={2} />}
        <Icon size={11} strokeWidth={2} />
        <span className="truncate">
          <code>{name}</code>
          {detail && (
            <>
              <span className="text-app-subtle"> → </span>
              <code className="text-app-text">{detail}</code>
            </>
          )}
          {isError && <span className="text-app-subtle"> · errored</span>}
        </span>
        {timestamp && (
          <span className="ml-auto text-[10px] text-app-subtle">
            {new Date(timestamp).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-1.5 space-y-2">
          {toolCall && (
            <Panel label="Input">
              {JSON.stringify(toolCall.input ?? {}, null, 2)}
            </Panel>
          )}
          {fallbackRequest && (
            <Panel label="Request">
              {JSON.stringify(fallbackRequest, null, 2)}
            </Panel>
          )}
          {outputPreview && (
            <PreviewPanel content={outputPreview} />
          )}
          <JsonPanel label={outputPreview ? `Raw ${isError ? "error" : "output"}` : isError ? "Error" : "Output"} collapsed={!!outputPreview}>
            {JSON.stringify(toolResult.output ?? {}, null, 2)}
          </JsonPanel>
        </div>
      )}
    </div>
  )
}

// Pulls a one-word qualifier off the tool's input/output for chip
// display. Keep this short — anything multi-word belongs in the
// expanded panel, not the header line.
//
// Add cases here when a new tool has a single primary parameter that
// changes the meaning of the call ("which endpoint", "which faq",
// "which topic"). Generic tools without such a parameter return null
// and the chip shows the tool name alone.
function extractToolDetail(
  name: string,
  input: unknown,
  output: unknown,
): string | null {
  const inObj  = (input  && typeof input  === "object") ? input  as Record<string, unknown> : null
  const outObj = (output && typeof output === "object") ? output as Record<string, unknown> : null

  switch (name) {
    case "call_company_endpoint": {
      const resultParams = (outObj?.params && typeof outObj.params === "object")
        ? (outObj.params as Record<string, unknown>)
        : null
      const fromCall   = typeof inObj?.endpoint_key === "string" ? (inObj.endpoint_key as string) : null
      const fromResult = typeof outObj?.endpoint === "string" ? (outObj.endpoint as string) : null
      const fromResultKey = typeof outObj?.endpoint_key === "string" ? (outObj.endpoint_key as string) : null
      const fromResultParams = typeof resultParams?.endpoint_key === "string" ? (resultParams.endpoint_key as string) : null
      return fromCall ?? fromResult ?? fromResultKey ?? fromResultParams
    }
    case "lookup_faq": {
      const id = inObj?.id ?? inObj?.faq_id
      return id != null ? String(id) : null
    }
    case "list_topics": {
      const tag = inObj?.tag ?? (Array.isArray(inObj?.tags) ? (inObj?.tags as unknown[]).join(",") : null)
      const search = typeof inObj?.search === "string" ? inObj.search : null
      return (tag as string | null) ?? search
    }
    case "check_policy": {
      const policy = inObj?.policy_name ?? inObj?.policy
      return typeof policy === "string" ? policy : null
    }
    case "set_custom_field": {
      const key = inObj?.key ?? inObj?.field
      return typeof key === "string" ? key : null
    }
    case "list_feedback": {
      const kw = Array.isArray(inObj?.keywords) ? (inObj?.keywords as unknown[]).slice(0, 2).join(",") : null
      return kw && kw.length > 0 ? kw : null
    }
    case "submit_feedback": {
      // Category is the active skill's key — operators don't pass it.
      // Surface a one-word distillation of keywords if present.
      const kw = Array.isArray(inObj?.keywords) ? (inObj?.keywords as unknown[]).slice(0, 2).join(",") : null
      return kw && kw.length > 0 ? kw : null
    }
    case "escalate": {
      const u = typeof inObj?.urgency === "string" ? inObj.urgency : null
      return u
    }
    default:
      return null
  }
}

function extractToolRequest(name: string, output: unknown): Record<string, unknown> | null {
  if (name !== "call_company_endpoint") return null

  const outObj = (output && typeof output === "object") ? output as Record<string, unknown> : null
  if (!outObj) return null

  const params = (outObj.params && typeof outObj.params === "object")
    ? (outObj.params as Record<string, unknown>)
    : null
  let endpoint: string | null = null
  if (typeof outObj.endpoint === "string") {
    endpoint = outObj.endpoint
  } else if (typeof outObj.endpoint_key === "string") {
    endpoint = outObj.endpoint_key
  } else if (typeof params?.endpoint_key === "string") {
    endpoint = params.endpoint_key
  }

  const request: Record<string, unknown> = {}
  if (endpoint) request.endpoint = endpoint
  if (params) request.params = params
  return Object.keys(request).length > 0 ? request : null
}

const PREVIEW_MAX_CHARS = 4_000
const PREVIEW_KEYS = ["details", "message", "text", "answer", "body", "content", "summary", "description", "error"]

function extractToolOutputPreview(name: string, output: unknown): string | null {
  if (name === "list_topics") return listTopicsPreview(output)
  if (name === "lookup_faq") return lookupFaqPreview(output)

  const text = findPreviewText(output)
  return text ? clampPreview(text) : null
}

function lookupFaqPreview(output: unknown): string | null {
  const record = asRecord(output)
  const details = stringValue(record.details) || stringValue(record.message) || stringValue(record.text)
  if (!details) return null

  const title = stringValue(record.name)
  return clampPreview(title ? `**${title}**\n\n${details}` : details)
}

function listTopicsPreview(output: unknown): string | null {
  const rawTopics = asRecord(output).topics
  const topics: unknown[] = Array.isArray(rawTopics) ? rawTopics : []
  if (topics.length === 0) return null

  const lines = topics.flatMap((topic) => {
    const record = asRecord(topic)
    const name = stringValue(record.name) || stringValue(record.key)
    if (!name) return []
    const description = stringValue(record.description)
    return description ? [`- **${name}** - ${description}`] : [`- **${name}**`]
  })
  return lines.length > 0 ? clampPreview(lines.join("\n")) : null
}

function findPreviewText(value: unknown, depth = 0): string | null {
  if (typeof value === "string") return humanText(value)
  if (depth > 3) return null
  if (!value || typeof value !== "object" || Array.isArray(value)) return null

  const record = value as Record<string, unknown>
  for (const key of PREVIEW_KEYS) {
    const text = humanText(record[key])
    if (text) return text
  }
  for (const key of PREVIEW_KEYS) {
    const nested = findPreviewText(record[key], depth + 1)
    if (nested) return nested
  }
  return null
}

function humanText(value: unknown): string | null {
  if (typeof value !== "string") return null
  const text = value.trim()
  if (!text) return null
  if (looksLikeJson(text)) return null
  return text
}

function looksLikeJson(text: string): boolean {
  const trimmed = text.trim()
  return (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function clampPreview(text: string): string {
  return text.length > PREVIEW_MAX_CHARS ? `${text.slice(0, PREVIEW_MAX_CHARS).trimEnd()}...` : text
}

function PreviewPanel({ content }: { content: string }) {
  return (
    <div>
      <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-subtle">Preview</div>
      <div className="rounded bg-app-bg p-2 text-app-text">
        <MessageMarkdown content={content} />
      </div>
    </div>
  )
}

function Panel({ label, children }: { label: string; children: string }) {
  return (
    <div>
      <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-subtle">{label}</div>
      <pre className="max-h-48 overflow-auto rounded bg-app-bg p-2 text-[10px] text-app-muted">{children}</pre>
    </div>
  )
}

function JsonPanel({ label, children, collapsed = false }: { label: string; children: string; collapsed?: boolean }) {
  if (collapsed) {
    return (
      <details>
        <summary className="cursor-pointer text-[10px] uppercase tracking-wide text-app-subtle">{label}</summary>
        <pre className="mt-1 max-h-48 overflow-auto rounded bg-app-bg p-2 text-[10px] text-app-muted">{children}</pre>
      </details>
    )
  }

  return <Panel label={label}>{children}</Panel>
}
